import {Suspense, useEffect, useRef} from "react";
import {useElementSizeStore, useFindOrganizationsMapStore, useMapRefStore, useMapStore,} from "../store.jsx";
import {useIntersectionObserver, useMediaQuery} from "usehooks-ts";
import {OrganizationCardMemo} from "../../OrganizationCard/OrganizationCard.jsx";
import HorizontalLine from "../../HorizontalLine/HorizontalLine.tsx";

import classNames from "classnames";

import s from "./ResultsContainer.module.css";
import NoResults from "../../NoResults/NoResults";
import Each from "../../utils/Each.tsx";
import OrganizationIterator from "../../utils/organizationIterator";
import debounce from "../../utils/debounce";

export default function ResultsContainer() {
    
    const resultContainerRef = useRef(null);
    const organizations = useFindOrganizationsMapStore(
        (state) => state.organizations,
    );
    const setSelectedItem = useFindOrganizationsMapStore(
        (state) => state.setSelectedItem,
    );
    const showMap = useFindOrganizationsMapStore((state) => state.showMap);
    const setShowMap = useFindOrganizationsMapStore(
        (state) => state.setShowMap,
    );

    const expandendResults = useFindOrganizationsMapStore(
        (state) => state.expandendResults,
    );
    const setResultsContainerRef = useMapRefStore(
        (state) => state.setResultsContainerRef,
    );
    const menuContainerHeight = useElementSizeStore(
        (state) => state.menuContainerHeight,
    );
    const setResultsContainerHeight = useElementSizeStore(
        (state) => state.setResultsContainerHeight,
    );
    const heightResultContainer = useElementSizeStore(
        (state) => state.resultsContainerHeight,
    );

    const isLoading = useFindOrganizationsMapStore((state) => state.isLoading);

    const isDesktopSize = useMediaQuery("(min-width: 992px)");
    const setMapPosition = useFindOrganizationsMapStore(
        (state) => state.setMapPosition,
    );
    const map = useMapStore((state) => state.map);
    const {
        findOrganizationGroup,
        setCurrentPage,
        setLastPage,
        setShowCardPagination,
        setMarkerGroups,
    } = useFindOrganizationsMapStore();

    
    const [visibleItems, setVisibleItems] = React.useState([]);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const iterator = React.useMemo(() => {
        setVisibleItems([])
        return OrganizationIterator(organizations?.result || [], 100)
    }, [organizations?.result]);
    const getNextItems = () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
    }
    
    useEffect(() => {
        setIsLoadingMore(false);
    }, [visibleItems]);
    
    useEffect(() => {
        if(!isLoadingMore) return;
        const {value, done} = iterator.next();

        if (done) {
            setIsLoadingMore(false);
            return;
        }
        setVisibleItems([...visibleItems, ...value]);
    }, [isLoadingMore]);

    const scrollHandler = React.useCallback(debounce(() => {
        if(isLoadingMore) return;
        const isNearBottom =
            resultContainerRef.current.scrollHeight -
            resultContainerRef.current.scrollTop <=
            resultContainerRef.current.clientHeight + 100;
        if (isNearBottom) {
            getNextItems();
        }
    }, 20), [organizations?.result]);

    useEffect(() => {
        if (!resultContainerRef.current) return;

        resultContainerRef.current.addEventListener("scroll", scrollHandler);

        return () => {
            if (resultContainerRef.current) {
                resultContainerRef.current.removeEventListener("scroll", scrollHandler);
            }
        };
    }, [scrollHandler]);
    
    const handleOrganizationClick = (organization) => {
        const currentGrouping =
            findOrganizationGroup(organization);

        if (currentGrouping.length > 1) {
            setShowCardPagination(true);
            setCurrentPage(
                currentGrouping.indexOf(
                    organization,
                ) + 1,
            );
            setLastPage(currentGrouping.length);
            setMarkerGroups(currentGrouping);
        } else {
            setShowCardPagination(false);
            setCurrentPage(1);
            setLastPage(1);
            setMarkerGroups([]);
        }

        setSelectedItem(organization);
        setShowMap(true);
        setMapPosition({
            lat: organization?.geoLocation
                ?.latitude,
            lng: organization?.geoLocation
                ?.longitude,    
        });
    }
    
    useEffect(() => {
        if (!organizations?.result?.length) {
            setVisibleItems([]);
        }
    }, [organizations?.result]);
    
    useEffect(() => {
        getNextItems();
    }, [iterator]);
        
    useEffect(() => {
        if (!resultContainerRef.current) return;

        setResultsContainerRef(resultContainerRef);
        resultContainerRef.current.addEventListener("scroll", scrollHandler);

        return () => {
            if (resultContainerRef.current) {
                resultContainerRef.current.removeEventListener(
                    "scroll",
                    scrollHandler,
                );
            }
        }
    }, [resultContainerRef.current]);

    useEffect(() => {
        if (!resultContainerRef.current) return;

        const height = resultContainerRef.current.clientHeight;

        setResultsContainerHeight(height);
    }, [resultContainerRef.current]);

    const animationStyles = {
        overflowY: "auto",
        maxHeight: `calc(100% - ${menuContainerHeight}px)`,
    };

    const resultContainerClasses = classNames(s.root, {
        [s.AutoHeight]:
            !organizations?.result?.length > 0 ||
            (heightResultContainer + menuContainerHeight < 710 &&
                !window.innerHeight > 832),
        [s.OverflowScroll]: !isDesktopSize && !showMap,
    });

    return (
        <Suspense fallback={<div>Loading...</div>}>
            {!!organizations?.result?.length > 0 &&
            expandendResults &&
            (isDesktopSize || !showMap) ? (
                <div
                    ref={resultContainerRef}
                    className={resultContainerClasses}
                    style={animationStyles}
                >
                    {!!resultContainerRef?.current && (
                    <Each
                        of={visibleItems}
                        render={(organization, index) => {
                            return (
                                <>
                                    <OrganizationCardMemo
                                        organization={organization}
                                        onClick={() => handleOrganizationClick(organization)}
                                        forceHidePagination
                                        hide={false}
                                        from={"results"}
                                    />
                                    {index !== organizations?.result.length - 1 && (
                                        <HorizontalLine
                                            color="--neutral-greyscale-100"
                                            thickness="1px"
                                            key={`hr-${index}.${organization?.name}`}
                                        />
                                    )}
                                </>)
                        }}
                    />)}
                </div>
            ) : (
                !expandendResults && (
                    <div className={s.info_box}>
                        <HorizontalLine
                            color="--neutral-greyscale-100"
                            thickness="1px"
                            marginBottom="8px"
                        />
                        <span className={s.padding}>
                            Använd sökfältet eller välj bransch för att visa
                            resultat.
                        </span>
                    </div>
                )
            )}
            {!organizations?.result?.length &&
                expandendResults &&
                !isLoading && (
                    <NoResults
                        text="Vi kunde inte hitta något som matchar din sökning."
                        icon="globe"
                    />
                )}
        </Suspense>
    );
}
